import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { TbFileDownload } from "react-icons/tb";
import JSZip from 'jszip';
import { saveAs } from "file-saver"
import Nav from '../components/Nav/Nav';
import Loading from "../components/General/Loading"
import UploadAuthModal from '../components/Upload/UploadAuthModal';
import Error404 from './Error404';
import { Button, Menu, MenuHandler, MenuItem, MenuList } from '@material-tailwind/react';
import Footer from '../components/Footer/Footer';

export default function Upload() {
  const [uploadState, setUploadState] = useState("loading")
  const [uploadType, setUploadType] = useState(null)
  const [uploadContent, setUploadContent] = useState(null);
  const [uploadTitle, setUploadTitle] = useState(null);
  const [uploadDescription, setUploadDescription] = useState(null);
  const [uploadCreatedAt, setUploadCreatedAt] = useState(null);
  const [uploadExpiry, setUploadExpiry] = useState(null);
  const [uploadPassword, setUploadPassword] = useState(null);
  const [actionButtonState, setActionButtonState] = useState(false);
  const actionButtonRef = useRef(null);
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const isValid = /^[a-zA-Z0-9]{4,15}$/.test(uploadId);

  useEffect(() => {
    getUploadContent();
  }, []);

  async function getUploadContent() {
    try {
      const response = await fetch(`https://uploads.jellyo.net/api/upload/${uploadId}`, { credentials: 'include' });
      const responseData = await response.json();
      if (responseData.status === "passwordRequired") {
        setUploadState("foundUpload");
        setTimeout(() => {
          setUploadState("passwordRequired");
        }, 1); // Timeout to remove errors in console
      } else if (responseData.status === "error") {
        setUploadState("notFound");
      } else if (responseData.status === "success") {
        setUploadState("foundUpload");
        setUploadType(responseData.uploadType);
        setUploadContent(responseData.uploadContent);
        setUploadTitle(responseData.uploadTitle);
        setUploadDescription(responseData.uploadDescription);
        setUploadCreatedAt(responseData.uploadCreatedAt);
        setUploadExpiry(responseData.uploadExpiry);
        setUploadPassword(responseData.uploadPassword);
      }
    } catch (error) {
      console.error("An error occurred while fetching uploadContent:", error);
      setTimeout(getUploadContent, 2000); // Retry every 2 seconds
    }
  }

  async function authenticatePassword(password) {
    const response = await fetch("https://uploads.jellyo.net/api/auth/", {
      method: "post",
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        uploadId,
        password
      })
    });
    const responseData = await response.json();
    if (responseData.status === "success") {
      getUploadContent();
      return true;
    } else {
      return responseData.message;
    }
  }

  function handleOpenSettings() {
    // TODO: Implement settings
    // For text, left, center, right alignment
    // Change font size?
    // Toggle displaying description?
    // For upload owner, allow deletion of upload
    // For upload owner, allow editing text uploads
    // For upload owner, allow adding/deleting files
  }

  function handleCopyText() {
    navigator.clipboard.writeText(uploadContent);

    setActionButtonState(true);
    if (actionButtonRef.current) clearTimeout(actionButtonRef.current);
    actionButtonRef.current = setTimeout(() => {
      setActionButtonState(false);
    }, 2000)
  }

  async function handleFileDownload() {
    if (uploadType !== "files") return;

    setActionButtonState(true);
    if (actionButtonRef.current) clearTimeout(actionButtonRef.current);
    actionButtonRef.current = setTimeout(() => {
      setActionButtonState(false);
    }, 2000)

    const zip = new JSZip();

    for (const file of uploadContent) {
      const response = await fetch(file.href, { credentials: 'include' });
      const blob = await response.blob();
      zip.file(file.filename, blob)
    }

    zip.generateAsync({ type: "blob" }).then(content => {
      saveAs(content, "download.zip")
    })
  }

  function getSecondsLeft(date1, date2 = new Date()) {
    const firstDate = new Date(date1);
    const secondDate = new Date(date2);

    const earlierDate = firstDate < secondDate ? firstDate : secondDate;
    const laterDate = firstDate < secondDate ? secondDate : firstDate;
    const differenceInSeconds = Math.round((laterDate - earlierDate) / 1000); // Convert milliseconds to seconds
    return differenceInSeconds;
  }

  function formatTime(seconds) {
    const secondsInAMinute = 60;
    const secondsInAnHour = 3600;
    const secondsInADay = 86400;
    const secondsInAWeek = 604800;
  
    if (seconds >= secondsInAWeek) {
      const weeks = (seconds / secondsInAWeek).toFixed(0);
      return `${weeks} week${weeks > 1 ? 's' : ''}`;
    } else if (seconds >= secondsInADay) {
      const days = (seconds / secondsInADay).toFixed(0);
      return `${days} day${days > 1 ? 's' : ''}`;
    } else if (seconds >= secondsInAnHour) {
      const hours = (seconds / secondsInAnHour).toFixed(0);
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else if (seconds >= secondsInAMinute) {
      const minutes = (seconds / secondsInAMinute).toFixed(0);
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      return `${seconds} second${seconds > 1 ? 's' : ''}`;
    }
  }

  function formatFileSize(bytes, round) {
    const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 bytes';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    if (i === 0) return `${bytes} ${units[i]}`; // No decimal for bytes
    return `${(bytes / Math.pow(1024, i)).toFixed(round || 0)} ${units[i]}`;
  }

  function showUploadContent() {
    if (uploadState === "foundUpload") {
      if (uploadType === "text") return uploadContent;
      if (uploadType === "files") return uploadContent.map((file) => (
        <a 
          className="group relative p-4 text-wrap break-words lg:min-h-[15rem] lg:min-w-[15rem] flex flex-col justify-center items-center rounded-lg transition-all" 
          key={file.filename} href={file.href} download={file.filename}
        >
          <div className='w-full h-full rounded-lg text-white opacity-0 absolute flex flex-col justify-center items-center bg-black z-10 transition-opacity duration-200 group-hover:opacity-80'>
            <span className='w-4/5 opacity-100 text-center text-lg'>{file.filename}</span>
            <span className='w-4/5 opacity-100 mb-1 text-center'>{formatFileSize(file.fileSize, 2)}</span>
            <span className='opacity-100'>Click to Download</span>
          </div>
          <TbFileDownload className='absolute h-20 w-20 group-hover:animate-jello text-gray-900' />
          <span className='pt-40 w-4/5 text-center group-hover:invisible'>{file.filename}</span>
          <span className='w-4/5 opacity-100 mb-1 text-center text-sm group-hover:invisible'>{formatFileSize(file.fileSize, 2)}</span>
        </a>
      ));
    } else if (uploadState === "passwordRequired") {
      return "NOT AUTHORISED";
    }
  }

  function IconButton({ active = true, iconClass, conditionClasses, onClick, hiddenCondition, showOnSmallScreen, disabled }) {
    if (!active) return;
    const baseClass = "w-10 h-10 flex justify-center items-center rounded-full bg-gray-900 cursor-pointer transition hover:scale-105";
    const visibilityClass = hiddenCondition ? "hidden" : "";
    const responsiveClass = showOnSmallScreen !== undefined ? (showOnSmallScreen ? "block sm:hidden" : "hidden sm:flex") : "";
  
    return (
      <div onClick={onClick} className={`${disabled ? "!cursor-not-allowed !opacity-50" : ""} ${baseClass} ${visibilityClass} ${conditionClasses} ${responsiveClass}`}>
        <i className={`${iconClass} text-xl text-white font-thin`}></i>
      </div>
    );
  }
  

  if (uploadState === "loading") return <Loading />

  if (!isValid || uploadState === "notFound") return <Error404 />;

  return (
    <div className={`overflow-x-hidden h-dvh w-screen bg-background-img bg-cover bg-repeat flex flex-col justify-center items-center`}>
      <Nav />
      <UploadAuthModal open={uploadState === "passwordRequired"} uploadId={uploadId} authenticatePassword={authenticatePassword} />
      <div className={`${uploadType === "text" || uploadState === "passwordRequired" ? "!w-max" : ""} max-w-[80rem] md:w-[70%] w-[90%] md:min-w-[40rem] max-h-[40rem] min-h-[10rem] m-4 mt-8 bg-offwhite rounded-xl shadow-all flex flex-col`}>

        <div className='p-4 w-full flex justify-between items-center border-b-2'>

          {uploadState === "foundUpload" ? <span className='ps-3 pe-6 truncate text-ellipsis'>{uploadTitle ? uploadTitle : (uploadType === "files" ? "Files upload" : "Text upload")}</span> : uploadState === "passwordRequired" ? "NOT AUTHORISED" : ""}
          
          <div className='flex justify-between items-center gap-2'>
            <Menu className="block sm:hidden" dismiss={{ itemPress: false }}>
              <MenuHandler>
                <Button disabled={uploadState === "passwordRequired"} size="sm" className={`${uploadState === "passwordRequired" ? "bg-red-500" : "bg-gray-900"} flex sm:hidden w-10 h-10 rounded-full justify-center items-center`}>
                  <i className={`${uploadState === "passwordRequired" ? "bx-x" : "bx-dots-vertical-rounded"} bx text-xl text-white font-thin`}></i>
                </Button>
              </MenuHandler>
              <MenuList className='min-w-min flex bg-gray-50 shadow-xl'>
                <MenuItem className='w-min p-1'>
                  <IconButton iconClass="bx bx-cog" showOnSmallScreen={true} active={false} />
                </MenuItem>
                <MenuItem className={`${uploadState !== "foundUpload" ? "hidden" : ""} w-min p-1`}>
                  <IconButton
                    onClick={uploadType === "files" ? handleFileDownload : handleCopyText}
                    conditionClasses={uploadState === "passwordRequired" ? "bg-red-500" : (actionButtonState ? "!bg-green-500" : "")}
                    iconClass={uploadState === "passwordRequired" ? "bx bx-x" : (uploadType === "files" && !actionButtonState ? "bx bxs-download" : uploadType === "text" && !actionButtonState ? "bx bx-clipboard" : "bx bx-check")}
                    showOnSmallScreen={true}
                  />
                </MenuItem>
              </MenuList>
            </Menu>
            <IconButton
              iconClass="bx bx-cog"
              showOnSmallScreen={false}
              active={false}
            />
            <IconButton
              onClick={uploadType === "files" ? handleFileDownload : handleCopyText}
              active={uploadState !== "passwordRequired"}
              conditionClasses={uploadState === "passwordRequired" ? "bg-red-500" : (actionButtonState ? "!bg-green-500" : "")}
              iconClass={uploadState === "passwordRequired" ? "bx bx-x" : (uploadType === "files" && !actionButtonState ? "bx bxs-download" : uploadType === "text" && !actionButtonState ? "bx bx-clipboard" : "bx bx-check")}
              hiddenCondition={uploadState !== "foundUpload"}
              showOnSmallScreen={false}
            />
          </div>
        </div>

        <div className={`${uploadType === "files" ? `grid grid-cols-auto-fit-minmax gap-2` : "flex justify-center"} overflow-y-auto my-2 px-4 sm:px-8 py-4 h-full w-full max-w-[80rem]`}>
          {showUploadContent()}
        </div>

        <div className='p-4 flex flex-col items-center border-t-2 text-gray-500'>
          <div className='flex flex-col md:gap-3 md:flex-row md:items-center mb-2 justify-center'>
            {uploadState === "foundUpload" ?
              <>
                <span className='sm:block'>Uploaded {uploadCreatedAt ? formatTime(getSecondsLeft(uploadCreatedAt)) : ""} ago</span>
                <span className='hidden md:inline'> • </span>
                <span className='sm:block'>Expires in {uploadExpiry ? formatTime(getSecondsLeft(uploadExpiry)) : ""}</span>
              </>
              : uploadState === "passwordRequired" ? "NOT AUTHORISED" : ""
            }
          </div>
          <span className='text-red-300 font-medium'>{uploadPassword ? "This is a Password Protected Upload" : ""}</span>
        </div>
      </div>
      <Footer />
    </div>
  )
}
